import { useRef, useState } from 'react'
import { ChevronDown, ChevronUp, CircleUserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  cancelAccountInvitation,
  inviteAccountMembersByEmail
} from '~/billing/services/billingService'
import { Button } from '~/components/common/Button'
import { SettingsSection } from '~/components/common/SettingsSection'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { EmailTagInput, EmailTagInputRef } from '~/components/common/EmailTagInput'
import { ConfirmationModal } from '~/components/modals/ConfirmationModal'
import { Typography } from '~/components/common/Typography'
import { useBillingContext } from '~/contexts/BillingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useUser } from '~/hooks/user/useUser'
import { useWorkspaceMembers } from '~/hooks/organization/useWorkspaceMembers'
import { I18nKey } from '~/i18n/declaration'
import { removeOrgUser, updateUserRole } from '~/services/organizationService'
import { WorkspaceMember } from '~/types/ResponseType'
import { BetterToast } from '~/utils/toast'
import { isWorkspaceAdmin } from '~/utils/userUtils'
import { cm } from '~/utils/utils'
import { isDefaultWorkspace } from '~/utils/workspaceUtils'

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Role hierarchy for sorting: account_admin > admin > user
 * Lower number = higher priority (shown first)
 */
function getRolePriority(role: string): number {
  switch (role) {
    case 'account_admin':
      return 0
    case 'admin':
      return 1
    case 'user':
      return 2
    default:
      return 3
  }
}

/**
 * Check if current user can take actions on a target user.
 * Account Admins are at the highest level - nobody can modify them.
 */
function canModifyUser(
  currentUserRole: string | undefined,
  isCurrentUserAccountAdmin: boolean,
  targetUser: WorkspaceMember
): boolean {
  if (targetUser.is_account_admin || targetUser.role === 'account_admin') {
    return false
  }
  if (isCurrentUserAccountAdmin) {
    return true
  }
  return currentUserRole === 'admin'
}

// ============================================================================
// Member Actions Menu
// ============================================================================

interface MemberActionsProps {
  member: WorkspaceMember
  canModify: boolean
  isSelf: boolean
  isUpdating: boolean
  onChangeRole: (role: 'admin' | 'user') => void
  onRemove: () => void
  onCancelInvite: () => void
}

function MoreActionsIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
      <circle cx='4' cy='8' r='1' fill='currentColor' />
      <circle cx='8' cy='8' r='1' fill='currentColor' />
      <circle cx='12' cy='8' r='1' fill='currentColor' />
    </svg>
  )
}

function MemberActions({
  member,
  canModify,
  isSelf,
  isUpdating,
  onChangeRole,
  onRemove,
  onCancelInvite
}: MemberActionsProps) {
  if (member.is_account_admin || member.role === 'account_admin') {
    return <div className='h-9 w-9' />
  }

  const isPending = member.status === 'pending'

  // For pending invitations, show cancel invite option
  if (isPending && member.invitation_id) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            className='flex h-9 w-9 min-w-0 items-center justify-center rounded-[4px] text-tokens-neutral-icon-hover disabled:cursor-not-allowed disabled:opacity-50'
            aria-label='Team member actions menu'
            disabled={isUpdating}
          >
            <MoreActionsIcon />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onClick={onCancelInvite}
            disabled={!canModify || isUpdating}
            className='text-tokens-status-error-text select-none'
          >
            Cancel Invite
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className='flex h-9 w-9 min-w-0 items-center justify-center rounded-[4px] text-tokens-neutral-icon-hover disabled:cursor-not-allowed disabled:opacity-50'
          aria-label='Team member actions menu'
          disabled={isUpdating}
        >
          <MoreActionsIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem
          onClick={() => member.role !== 'admin' && onChangeRole('admin')}
          disabled={!canModify || isUpdating}
          className='select-none'
        >
          Make Admin {member.role === 'admin' && '✓'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => member.role !== 'user' && onChangeRole('user')}
          disabled={!canModify || isUpdating}
          className='select-none'
        >
          Make User {member.role === 'user' && '✓'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onRemove}
          disabled={!canModify || isSelf || isUpdating}
          className='text-tokens-status-error-text select-none'
        >
          Remove User
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ============================================================================
// Members Table
// ============================================================================

interface MembersTableProps {
  members: WorkspaceMember[]
  onRefresh: () => void
}

function MembersTable({ members, onRefresh }: MembersTableProps) {
  const { t } = useTranslation()
  const currentUser = useUser()
  const { currentWorkspace } = useWorkspaceContext()
  const { billingInfo } = useBillingContext()

  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMember | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  // Sort by status (active first, then pending) and role priority
  const sortedMembers = [...members].sort((a, b) => {
    // Active users come first
    if (a.status === 'active' && b.status === 'pending') return -1
    if (a.status === 'pending' && b.status === 'active') return 1
    // Then sort by role priority
    return getRolePriority(a.role) - getRolePriority(b.role)
  })

  // Check if current user is an account admin
  const currentUserData = members.find(m => m.id === currentUser?.user_id)
  const isCurrentUserAccountAdmin =
    currentUserData?.is_account_admin || currentUserData?.role === 'account_admin'

  const canCurrentUserModifyMember = (member: WorkspaceMember): boolean =>
    canModifyUser(currentWorkspace?.user_role, isCurrentUserAccountAdmin, member)

  const handleUpdateRole = async (userId: number, role: 'admin' | 'user') => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    const targetMember = members.find(member => member.id === userId)
    if (!targetMember) {
      BetterToast.error('User not found')
      return
    }

    if (!canCurrentUserModifyMember(targetMember)) {
      BetterToast.error('You do not have permission to modify this member')
      return
    }

    setIsUpdating(true)
    try {
      await updateUserRole(currentWorkspace.uuid, userId, role)
      BetterToast.success('User role updated')
      onRefresh()
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to update user role')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRemoveUser = async () => {
    if (!currentWorkspace?.uuid || !memberToRemove?.id) {
      BetterToast.error('Workspace or user not found')
      return
    }

    if (memberToRemove.id === currentUser?.user_id) {
      BetterToast.error('You cannot remove yourself from the workspace')
      return
    }

    if (!canCurrentUserModifyMember(memberToRemove)) {
      BetterToast.error('You do not have permission to remove this member')
      return
    }

    setIsRemoving(true)
    try {
      await removeOrgUser(currentWorkspace.uuid, memberToRemove.id)
      BetterToast.success('User removed from workspace')
      setMemberToRemove(null)
      onRefresh()
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to remove user')
    } finally {
      setIsRemoving(false)
    }
  }

  const handleCancelInvite = async (invitationId: number) => {
    if (!billingInfo?.id) {
      BetterToast.error('Billing account not found')
      return
    }

    const invitationMember = members.find(member => member.invitation_id === invitationId)
    if (!invitationMember || !canCurrentUserModifyMember(invitationMember)) {
      BetterToast.error('You do not have permission to cancel this invitation')
      return
    }

    setIsUpdating(true)
    try {
      await cancelAccountInvitation(billingInfo.id, invitationId)
      BetterToast.success('Invitation cancelled')
      onRefresh()
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to cancel invitation')
    } finally {
      setIsUpdating(false)
    }
  }

  const tableRows = sortedMembers.map(member => {
    const canModify = canModifyUser(currentWorkspace?.user_role, isCurrentUserAccountAdmin, member)
    const isSelf = member.id === currentUser?.user_id
    const displayRole =
      member.is_account_admin || member.role === 'account_admin'
        ? 'Account Admin'
        : member.role === 'admin'
          ? 'Admin'
          : 'User'
    const isPending = member.status === 'pending'
    const key = member.id ?? member.email

    return { key, member, canModify, isSelf, displayRole, isPending }
  })

  const tableColumnClass =
    'grid w-full grid-cols-[minmax(0,1fr)_minmax(76px,120px)_60px] items-center gap-3 sm:gap-6 md:grid-cols-[minmax(0,1fr)_160px_60px]'

  return (
    <>
      <div className='flex flex-col gap-2 px-0 sm:px-2 md:px-8'>
        <div className={cm(tableColumnClass, 'h-10 pl-3 pr-0 sm:pl-4 sm:pr-0')}>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='truncate text-tokens-neutral-text-secondary'
          >
            Members
          </Typography>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='truncate text-tokens-neutral-text-secondary'
          >
            Role
          </Typography>
          <Typography
            fontFamily='geistMono'
            fontSize='labelMd'
            fontWeight='semibold'
            className='truncate text-right text-tokens-neutral-text-secondary'
          >
            Actions
          </Typography>
        </div>

        <div className='flex flex-col gap-1'>
          {tableRows.map(({ key, member, canModify, isSelf, displayRole, isPending }) => (
            <div
              key={key}
              className={cm(
                tableColumnClass,
                'h-10 rounded-xl pl-3 pr-0 sm:pl-4 sm:pr-0 transition-colors hover:bg-tokens-neutral-fill-fill-4 active:bg-tokens-neutral-fill-fill-6'
              )}
            >
              <div className='min-w-0'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='bold'
                  className={cm(
                    'truncate leading-[18px] text-tokens-neutral-text-primary',
                    isPending && 'italic text-tokens-neutral-text-tertiary'
                  )}
                >
                  {isPending ? 'Pending invitation' : member.name}
                </Typography>
                <Typography
                  fontFamily='geistMono'
                  fontSize='labelSm'
                  fontWeight='semibold'
                  className='truncate text-[11px] tracking-[-0.33px] text-tokens-neutral-text-tertiary'
                >
                  {member.email}
                </Typography>
              </div>

              <div className='min-w-0'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySmMedium'
                  className='truncate leading-4 text-tokens-neutral-text-primary'
                >
                  {displayRole}
                </Typography>
              </div>

              <div className='flex items-center justify-end'>
                <MemberActions
                  member={member}
                  canModify={canModify}
                  isSelf={isSelf}
                  isUpdating={isUpdating}
                  onChangeRole={role => member.id && handleUpdateRole(member.id, role)}
                  onRemove={() => setMemberToRemove(member)}
                  onCancelInvite={() =>
                    member.invitation_id && handleCancelInvite(member.invitation_id)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        title='Remove User?'
        content={`Are you sure you want to remove ${memberToRemove?.name || memberToRemove?.email} from this workspace?`}
        buttons={
          <div className='flex gap-[5px]'>
            <Button
              onClick={() => setMemberToRemove(null)}
              variant='outlined'
              isDisabled={isRemoving}
              className='select-none'
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveUser}
              variant='destructive'
              isDisabled={isRemoving}
              className='select-none'
            >
              {isRemoving ? 'Removing...' : 'Remove User'}
            </Button>
          </div>
        }
      />
    </>
  )
}

// ============================================================================
// Invite Members Section
// ============================================================================

interface InviteMembersSectionProps {
  onInviteSuccess: () => void
}

function InviteMembersSection({ onInviteSuccess }: InviteMembersSectionProps) {
  const { t } = useTranslation()
  const { currentWorkspace } = useWorkspaceContext()
  const { billingInfo, isBillingAdmin, licenses } = useBillingContext()

  const [inviteEmails, setInviteEmails] = useState<string[]>([])
  const [hasPendingInviteEmail, setHasPendingInviteEmail] = useState(false)
  const [isInviting, setIsInviting] = useState(false)
  const inviteInputRef = useRef<EmailTagInputRef>(null)

  // Check if the account has Team or Enterprise license
  // Invitation feature is only available for multi-user plans (Team, Enterprise)
  // Pro and Pro+ are single-user plans and don't support team invitations
  const hasTeamOrEnterpriseLicense = licenses.some(
    license =>
      license.license_name?.toLowerCase().includes('team') ||
      license.license_name?.toLowerCase().includes('enterprise')
  )

  // Only show invite section for:
  // 1. Non-default (non-personal) workspaces
  // 2. Workspace admins or account (billing) admins
  // 3. Team or Enterprise license (multi-user plans only)
  const isDefault = currentWorkspace ? isDefaultWorkspace(currentWorkspace) : true
  const isAdmin = isWorkspaceAdmin(currentWorkspace)
  const canInvite = !isDefault && (isAdmin || isBillingAdmin) && hasTeamOrEnterpriseLicense

  if (!canInvite || !billingInfo?.id || !currentWorkspace?.id) {
    return null
  }

  const handleInvite = async () => {
    if (!billingInfo?.id || !currentWorkspace?.id) return

    const pendingInviteEmail = inviteInputRef.current?.getPendingValidEmail() ?? null
    const emailsToInvite =
      pendingInviteEmail && !inviteEmails.includes(pendingInviteEmail)
        ? [...inviteEmails, pendingInviteEmail]
        : inviteEmails

    if (emailsToInvite.length === 0) return

    if (pendingInviteEmail) {
      inviteInputRef.current?.commitPendingEmail()
    }

    setIsInviting(true)
    try {
      const result = await inviteAccountMembersByEmail(billingInfo.id, {
        emails: emailsToInvite,
        invite_type: 'specific_workspaces',
        workspace_ids: [currentWorkspace.id]
      })

      if (result.invited.length > 0) {
        BetterToast.success(
          `Invitation sent to ${result.invited.length} user${result.invited.length > 1 ? 's' : ''}`
        )
        setInviteEmails([])
        setHasPendingInviteEmail(false)
        onInviteSuccess()
      }
      if (result.failed.length > 0) {
        BetterToast.error(`Failed to invite: ${result.failed.join(', ')}`)
      }
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to send invitations')
    } finally {
      setIsInviting(false)
    }
  }

  return (
    <div className='flex flex-col gap-3 pt-2'>
      <Typography
        fontFamily='geist'
        fontSize='bodySm'
        className='text-tokens-neutral-text-secondary select-none'
      >
        {t(I18nKey.ORGANIZATION$INVITE_MEMBER)}
      </Typography>
      <div className='flex items-center gap-2'>
        <EmailTagInput
          ref={inviteInputRef}
          emails={inviteEmails}
          onEmailsChange={emails => {
            setInviteEmails(emails)
            setHasPendingInviteEmail(false)
          }}
          onInputStateChange={({ hasValidPendingEmail }) => {
            setHasPendingInviteEmail(hasValidPendingEmail)
          }}
          onSubmit={handleInvite}
          placeholder='Enter email addresses'
          className='flex-1'
        />
        <Button
          variant='primary'
          className='h-[44px] px-5 whitespace-nowrap select-none'
          onClick={handleInvite}
          isDisabled={(inviteEmails.length === 0 && !hasPendingInviteEmail) || isInviting}
        >
          {isInviting ? 'Inviting...' : 'Invite'}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// MembersSection (Main Export)
// ============================================================================

export function MembersSection() {
  const { members, isLoading, refetch } = useWorkspaceMembers()
  const [isExpanded, setIsExpanded] = useState(true)

  const peopleLabel = `${members.length} ${members.length === 1 ? 'Member' : 'Members'}`

  if (isLoading) {
    return (
      <SettingsSection className='gap-0'>
        <div className='flex h-12 items-center justify-between'>
          <div className='flex items-center gap-4'>
            <span className='flex h-6 w-6 shrink-0 items-center justify-center text-tokens-neutral-icon-solid'>
              <CircleUserRound className='h-[18px] w-[18px]' />
            </span>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='px-2 text-tokens-neutral-text-primary'
            >
              Workspace Members
            </Typography>
          </div>
          <Typography
            fontFamily='geistMono'
            fontSize='labelLg'
            fontWeight='semibold'
            className='text-tokens-neutral-text-secondary'
          >
            Loading...
          </Typography>
        </div>
      </SettingsSection>
    )
  }

  return (
    <SettingsSection className='gap-0'>
      <button
        type='button'
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center gap-4 w-full h-12 cursor-pointer'
      >
        <div className='flex min-w-0 flex-1 items-center gap-4'>
          <span className='flex h-6 w-6 shrink-0 items-center justify-center text-tokens-neutral-icon-solid'>
            <CircleUserRound className='h-[18px] w-[18px]' />
          </span>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='medium'
            className='truncate px-2 text-left text-tokens-neutral-text-primary'
          >
            Workspace Members
          </Typography>
        </div>

        <div className='flex items-center gap-2 h-12 py-2 px-1 rounded-xl justify-end cursor-pointer hover:opacity-80 transition-opacity min-w-[160px] max-w-[360px]'>
          <span className='font-mono font-semibold text-[15px] leading-normal text-tokens-neutral-text-secondary overflow-hidden text-ellipsis whitespace-nowrap'>
            {peopleLabel}
          </span>
          <span className='flex items-center justify-center'>
            {isExpanded ? (
              <ChevronUp className='w-[18px] h-[18px] text-tokens-neutral-icon-solid' />
            ) : (
              <ChevronDown className='w-[18px] h-[18px] text-tokens-neutral-icon-solid' />
            )}
          </span>
        </div>
      </button>

      {isExpanded && (
        <>
          <div className='border-t border-tokens-neutral-border-2' />
          <div className='pt-4'>
            <InviteMembersSection onInviteSuccess={refetch} />
            <MembersTable members={members} onRefresh={refetch} />
          </div>
        </>
      )}
    </SettingsSection>
  )
}
